import {
  PutFeedbackCommand,
  PutFeedbackCommandInput,
  PutFeedbackCommandOutput,
} from '@amzn/gbsa-helios-typescript-client';
import { useMutation, UseMutationOptions, useQueryClient } from '@tanstack/react-query';
import { apiHeliosClient } from 'src/data/api/apiHeliosClient';
import { feedbackKeys } from 'src/data/api/helios-service-ts/feedback/queries/keys';
import { feedbackService } from 'src/data/api/helios-service-ts/feedback/service';

/**
 * React hook for creating a mutation to put/update feedback.
 *
 * This hook wraps React Query's `useMutation` to provide a mutation function
 * for submitting feedback to the Helios service. On successful mutation, it
 * automatically invalidates the corresponding feedback query to trigger a re-fetch.
 *
 * @param key - The feedback key parameters (all fields from PutFeedbackCommandInput except 'feedback').
 *              Used to identify the mutation and the associated query to invalidate.
 * @param options - Optional React Query mutation options. Note that 'mutationKey' and 'mutationFn'
 *                  are handled internally and should not be provided.
 * @returns A React Query mutation object with methods to trigger the mutation (mutate, mutateAsync)
 *          and access mutation state (status, error, data, etc.).
 *
 * @example
 * ```tsx
 * const { mutate, status, error } = usePutFeedbackMutation(
 *   { auditId: '123', questionId: '456', createdBy: 'user@example.com' },
 *   {
 *     onSuccess: () => {
 *       console.log('Feedback submitted successfully');
 *     },
 *   }
 * );
 *
 * // Later, trigger the mutation
 * mutate({
 *   auditId: '123',
 *   questionId: '456',
 *   createdBy: 'user@example.com',
 *   feedback: 'This is my feedback text',
 * });
 * ```
 */
export const usePutFeedbackMutation = (
  key: Pick<
    PutFeedbackCommandInput,
    'auditId' | 'businessUnitId' | 'createdBy' | 'divisionId' | 'questionId'
  >,
  options?: Omit<
    UseMutationOptions<PutFeedbackCommandOutput, Error, PutFeedbackCommandInput>,
    'mutationKey' | 'mutationFn'
  >,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    ...options,
    mutationKey: feedbackKeys.put(key),
    mutationFn: (data: PutFeedbackCommandInput) => feedbackService.put(data),
    async onSuccess(data, variables, onMutateResult, context) {
      // always invalidate the record we mutated to force a re-fetch
      await queryClient.invalidateQueries({
        queryKey: feedbackKeys.get(variables),
      });
      await options?.onSuccess?.(data, variables, onMutateResult, context);
    },
  });
};
